import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#observeMobileHeight();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #mobileResize;
  #mobileMutation;
  #mobileFrame = 0;

  #observeMobileHeight() {
    const track = this.querySelector('slideshow-slides');
    if (!track || !this.classList.contains('media-gallery--carousel') || this.classList.contains('media-gallery--hint')) return;
    const update = () => {
      cancelAnimationFrame(this.#mobileFrame);
      this.#mobileFrame = requestAnimationFrame(() => {
        if (!matchMedia('(max-width: 749px)').matches) {
          track.style.removeProperty('height');
          return;
        }
        const slide = track.querySelector(':scope > slideshow-slide[aria-hidden="false"]');
        if (!slide) return;
        const ratio = parseFloat(getComputedStyle(slide).getPropertyValue('--media-preview-ratio'));
        if (ratio > 0 && track.clientWidth > 0) {
          track.style.height = `${track.clientWidth / ratio}px`;
        }
      });
    };
    this.#mobileMutation = new MutationObserver(update);
    this.#mobileMutation.observe(track, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
    this.#mobileResize = new ResizeObserver(update);
    this.#mobileResize.observe(this);
    update();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#mobileResize?.disconnect();
    this.#mobileMutation?.disconnect();
    cancelAnimationFrame(this.#mobileFrame);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
